import { PageSpinner, useNotifications } from '@armhr/ui';
import {
  CheckCircleOutline,
  Close,
  Download,
  ExpandLess,
  ExpandMore,
  FileUploadOutlined,
  MoreVert,
  SearchOutlined,
  TuneOutlined,
} from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Menu,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import React, {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Helmet } from 'react-helmet';

import AdminHandbookAssignModal from '../../components/admin/AdminHandbookAssignModal';
import AdminHandbookProgressBar from '../../components/admin/AdminHandbookProgressBar';
import EditAssignmentModal, {
  type EditAssignmentRow,
} from '../../components/admin/EditAssignmentModal';
import PdfPreviewCard from '../../components/common/PdfPreviewCard';
import { useApi } from '../../hooks/useApi';
import { useApiData } from '../../hooks/useApiData';
import { useUser } from '../../hooks/useUser';
import { formatKeyToHandbookTitle, isValidPDF } from '../../utils/handbooks';

const PdfReader = lazy(() => import('../../components/common/PdfReader'));

const AdminHandbooksPage: React.FC = () => {
  const api = useApi();
  const { user } = useUser();
  const { showNotification } = useNotifications();

  const isArmhrAdmin =
    user?.prism_roles?.user_role?.includes('ARMHRADMIN') || false;

  const [isReaderOpen, setIsReaderOpen] = useState<boolean>(false);
  const [isAssignOpen, setIsAssignOpen] = useState<boolean>(false);
  const [isPermissionsOpen, setIsPermissionsOpen] = useState<boolean>(false);
  const [selectedAction, setSelectedAction] = useState<
    'archive' | 'delete' | 'unarchive' | null
  >(null);
  const [selectedHandbook, setSelectedHandbook] =
    useState<HandbookAsset | null>();
  const [menuAnchorEl, setMenuAnchorEl] = useState<{
    [key: number]: HTMLElement | null;
  }>({});

  const [assignments, setAssignments] = useState<{
    [handbook_id: number]: EmployeeHandbookAssignment[];
  }>({});

  const [editingAssignment, setEditingAssignment] =
    useState<EditAssignmentRow | null>(null);

  const [unassignTarget, setUnassignTarget] = useState<{
    assignmentId: number;
    handbookId: number;
    employeeName: string;
  } | null>(null);

  const [fileUploading, setFileUploading] = useState<boolean>(false);
  const [isArchivedExpanded, setIsArchivedExpanded] = useState<boolean>(false);
  const [reminderHandbook, setReminderHandbook] =
    useState<HandbookAsset | null>(null);
  const [sendReminderTarget, setSendReminderTarget] = useState<{
    employeeId: string;
    handbookId: number;
    employeeName: string;
    handbookName: string;
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    data: handbooks,
    loading: handbooksLoading,
    error: handbooksError,
    refresh,
  } = useApiData((api) => api.company.getCompanyHandbooks());

  useEffect(() => {
    const fetchAndSetAssignments = async (id: number) => {
      try {
        const { results: assignments } =
          await api.company.getEmployeeAssignments(id);
        setAssignments((current) => ({ ...current, [id]: assignments }));
      } catch (e) {
        console.log(e);
      }
    };
    handbooks?.map((book) => fetchAndSetAssignments(book.id));
  }, [handbooks]);

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleHandbookChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFile = e.target.files[0];
      if (!isValidPDF(selectedFile.name)) {
        showNotification({
          message: 'Invalid file type. Handbook upload must be a PDF.',
          severity: 'error',
        });
        return;
      }

      const formData = new FormData();
      formData.append('handbook', selectedFile);

      try {
        setFileUploading(true);
        await api.company.uploadHandbook(formData);
        showNotification({
          message: 'Successfully uploaded handbook',
          severity: 'success',
        });
        refresh();
      } catch (error) {
        showNotification({
          message: 'Unable to upload handbook. Please try again.',
          severity: 'error',
        });
      } finally {
        setFileUploading(false);
      }
    }
  };

  const handleDeleteHandbook = async (handbook_id: number) => {
    try {
      const response = await api.company.deleteHandbook(handbook_id);
      response.success
        ? showNotification({
            message: 'Successfully deleted handbook',
            severity: 'success',
            autoHideDuration: 4000,
          })
        : showNotification({
            message: 'Unable to delete handbook. Please try again later.',
            severity: 'error',
          });
      refresh();
    } catch (e) {
      showNotification({
        message: 'Unable to delete handbook. Please try again later.',
        severity: 'error',
      });
    }
  };

  const handleArchiveHandbook = async (handbook: HandbookAsset) => {
    try {
      await api.company.archiveEmployeeHandbook(handbook.id, handbook);
      showNotification({
        message: 'Successfully updated archive status of handbook',
        severity: 'success',
      });
      refresh();
    } catch (e) {
      showNotification({
        message:
          'Unable to update archive status of handbook. Please try again later.',
        severity: 'error',
      });
    }
  };

  const handleSetReminder = async (days: number | null) => {
    if (!reminderHandbook) return;
    try {
      const { reminder_days_before_due: _, ...rest } =
        (reminderHandbook.config ?? {}) as Record<string, unknown>;
      const config =
        days !== null
          ? { ...rest, reminder_days_before_due: days }
          : Object.keys(rest).length > 0
          ? rest
          : null;
      await api.company.setHandbookAutoReminder(reminderHandbook, config);
      showNotification({
        message: days
          ? `Reminder set for ${days} day${days > 1 ? 's' : ''} before due date`
          : 'Reminder cleared',
        severity: 'success',
      });
      refresh();
    } catch (e) {
      showNotification({
        message: 'Unable to update reminder. Please try again later.',
        severity: 'error',
      });
    } finally {
      setReminderHandbook(null);
    }
  };

  const handleSendReminderClick = (
    employeeId: string,
    handbookId: number,
    employeeName: string,
    handbookName: string
  ) => {
    setSendReminderTarget({
      employeeId,
      handbookId,
      employeeName,
      handbookName,
    });
  };

  const confirmSendReminder = async () => {
    if (!sendReminderTarget) return;
    const { employeeId, handbookId } = sendReminderTarget;
    try {
      await api.company.sendHandbookReminder(employeeId, handbookId);
      showNotification({
        message: 'Reminder email sent',
        severity: 'success',
      });
      await refreshAssignments(handbookId);
    } catch (e) {
      showNotification({
        message: 'Unable to send reminder. Please try again later.',
        severity: 'error',
      });
    } finally {
      setSendReminderTarget(null);
    }
  };

  const handleDownload = (book: HandbookAsset) => {
    if (!book.presigned_url) return;
    const filename = formatKeyToHandbookTitle(book.key ?? '') || 'handbook.pdf';
    const link = document.createElement('a');
    link.href = book.presigned_url;
    link.download = filename.endsWith('.pdf') ? filename : `${filename}.pdf`;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const refreshAssignments = async (handbookId: number) => {
    try {
      const { results } = await api.company.getEmployeeAssignments(handbookId);
      setAssignments((current) => ({ ...current, [handbookId]: results }));
    } catch (e) {
      console.error(e);
    }
  };

  const confirmUnassign = async () => {
    if (!unassignTarget) return;
    try {
      await api.company.unassignEmployeeHandbook([unassignTarget.assignmentId]);
      showNotification({
        message: `${unassignTarget.employeeName} has been unassigned.`,
        severity: 'success',
        autoHideDuration: 4000,
      });
      await refreshAssignments(unassignTarget.handbookId);
    } catch {
      showNotification({
        message: 'Unable to unassign employee. Please try again.',
        severity: 'error',
      });
    } finally {
      setUnassignTarget(null);
      setEditingAssignment(null);
    }
  };

  if (handbooksLoading) return <PageSpinner />;
  return (
    <Box>
      <Helmet>
        <title>Company Handbooks | Armhr</title>
        <meta
          name="description"
          content="Manage and assign company handbooks to employees."
        />
      </Helmet>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h2">Company Handbooks</Typography>
      </Box>
      <Box sx={{ mb: 8 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', mb: 3 }}>
            <Typography variant="h4" sx={{ flexGrow: 1 }}>
              Active handbooks
            </Typography>
            <Typography variant="caption">
              Please note: only PDFs are supported. Employees must be assigned
              to a handbook in order to view it.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            {isArmhrAdmin && (
              <Button
                variant="outlined"
                onClick={() => setIsPermissionsOpen(true)}
                startIcon={<TuneOutlined />}
              >
                Manage Permissions
              </Button>
            )}
            <input
              type="file"
              accept=".pdf,application/pdf"
              ref={fileInputRef}
              style={{ display: 'none' }}
              onChange={handleHandbookChange}
            />
            <LoadingButton
              variant="contained"
              onClick={handleUploadClick}
              loading={fileUploading}
              startIcon={<FileUploadOutlined />}
            >
              Upload Handbook
            </LoadingButton>
          </Stack>
        </Box>
        <Box>
          {!!handbooksError ? (
            <Box>
              <Alert severity="error">
                Unable to load handbooks. Please try back again later.
              </Alert>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Active */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                {handbooks
                  ?.filter((book) => !book.archived)
                  .map((book, idx) => (
                    <Box key={idx}>
                      <PdfPreviewCard
                        pdfUrl={book.presigned_url || ''}
                        handleActionAreaClick={() => {
                          setSelectedHandbook(book);
                          setIsReaderOpen(true);
                        }}
                        cardProps={{
                          sx: {
                            display: 'flex',
                            flexDirection: 'column',
                            width: 300,
                            height: '100%',
                          },
                        }}
                        contentChildren={
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 2,
                              justifyContent: 'space-around',
                            }}
                          >
                            <Typography variant="h5" align="center">
                              {formatKeyToHandbookTitle(book.key ?? '')}
                            </Typography>
                          </Box>
                        }
                        actionChildren={
                          <Box
                            sx={{
                              width: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              px: 2,
                            }}
                          >
                            <Box sx={{ flex: 1 }} />
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => {
                                setIsAssignOpen(true);
                                setSelectedHandbook(book);
                              }}
                            >
                              Assign
                            </Button>
                            <Box
                              sx={{
                                flex: 1,
                                display: 'flex',
                                justifyContent: 'flex-end',
                              }}
                            >
                              <IconButton
                                onClick={(e) =>
                                  setMenuAnchorEl({
                                    [book.id]: e.currentTarget,
                                  })
                                }
                              >
                                <MoreVert />
                              </IconButton>
                            </Box>
                            <Menu
                              anchorEl={menuAnchorEl[book.id] || null}
                              open={Boolean(menuAnchorEl[book.id])}
                              onClose={() =>
                                setMenuAnchorEl({
                                  [book.id]: null,
                                })
                              }
                            >
                              <MenuItem
                                onClick={() => {
                                  setMenuAnchorEl({
                                    [book.id]: null,
                                  });
                                  handleDownload(book);
                                }}
                              >
                                Download
                              </MenuItem>
                              <MenuItem
                                onClick={() => {
                                  setMenuAnchorEl({
                                    [book.id]: null,
                                  });
                                  setReminderHandbook(book);
                                }}
                              >
                                Set reminder email
                              </MenuItem>
                              <MenuItem
                                onClick={() => {
                                  setMenuAnchorEl({
                                    [book.id]: null,
                                  });
                                  setSelectedHandbook({
                                    ...book,
                                    archived: true,
                                  });
                                  setSelectedAction('archive');
                                }}
                              >
                                Archive
                              </MenuItem>
                              <MenuItem
                                onClick={() => {
                                  setMenuAnchorEl({
                                    [book.id]: null,
                                  });
                                  setSelectedHandbook(book);
                                  setSelectedAction('delete');
                                }}
                              >
                                Delete
                              </MenuItem>
                            </Menu>
                          </Box>
                        }
                      />
                    </Box>
                  ))}
              </Box>

              {!!selectedHandbook && (
                <>
                  <AdminHandbookAssignModal
                    open={isAssignOpen}
                    handleClose={() => {
                      setIsAssignOpen(false);
                      setSelectedHandbook(null);
                    }}
                    handleUpdateAssign={setAssignments}
                    assignments={assignments}
                    handbookId={selectedHandbook?.id!}
                    handbookName={formatKeyToHandbookTitle(
                      selectedHandbook?.key ?? ''
                    )}
                  />

                  {isReaderOpen && (
                    <Dialog open={isReaderOpen} maxWidth="lg">
                      <DialogContent>
                        <Suspense fallback={<PageSpinner />}>
                          <PdfReader pdfUrl={selectedHandbook.presigned_url!} />
                        </Suspense>
                      </DialogContent>
                      <DialogActions>
                        <Box
                          display="flex"
                          sx={{
                            width: '100%',
                            justifyContent: 'right',
                            gap: 2,
                          }}
                        >
                          <Button
                            variant="outlined"
                            startIcon={<Download />}
                            onClick={() => handleDownload(selectedHandbook)}
                          >
                            Download
                          </Button>
                          <Button
                            variant="contained"
                            onClick={() => {
                              setIsReaderOpen(false);
                              setSelectedHandbook(null);
                            }}
                          >
                            Close
                          </Button>
                        </Box>
                      </DialogActions>
                    </Dialog>
                  )}

                  <HandbookActionDialog
                    handbook={selectedHandbook}
                    action={selectedAction}
                    onClose={() => {
                      setSelectedHandbook(null);
                      setSelectedAction(null);
                    }}
                    onArchive={handleArchiveHandbook}
                    onDelete={handleDeleteHandbook}
                  />
                </>
              )}

              {reminderHandbook && (
                <ReminderDialog
                  handbook={reminderHandbook}
                  onClose={() => setReminderHandbook(null)}
                  onSave={handleSetReminder}
                />
              )}
            </Box>
          )}
        </Box>
      </Box>
      {/* Assignment progress */}
      {handbooks &&
        handbooks.length > 0 &&
        handbooks.some(
          (book) => !book.archived && assignments[book.id]?.length > 0
        ) && (
          <Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', mb: 3 }}>
              <Typography variant="h4" sx={{ flexGrow: 1 }}>
                Handbook assignment progress
              </Typography>
            </Box>
            <Box
              display="flex"
              flexDirection="column"
              gap={2}
              justifyContent="center"
            >
              {handbooks
                .filter((handbook) => !handbook.archived)
                .map((handbook, idx) => (
                  <AdminHandbookProgressBar
                    key={`${handbook.id}-${idx}-progress`}
                    handbook={handbook}
                    assignments={assignments[handbook.id]}
                    onEditAssignment={(row) => setEditingAssignment(row)}
                    onUnassignAssignment={(
                      assignmentId,
                      handbookId,
                      employeeName
                    ) =>
                      setUnassignTarget({
                        assignmentId,
                        handbookId,
                        employeeName,
                      })
                    }
                    onSendReminder={handleSendReminderClick}
                  />
                ))}
            </Box>
          </Box>
        )}

      {/* Archived handbooks (bottom of page) */}
      {handbooks?.some((book) => book.archived) && (
        <Box sx={{ mt: 6 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              mb: 2,
              p: 2,
              '&:hover': {
                backgroundColor: '#fafafa',
              },
            }}
            onClick={() => setIsArchivedExpanded(!isArchivedExpanded)}
          >
            <Typography variant="h4" sx={{ flexGrow: 1 }}>
              Archived handbooks
            </Typography>
            {isArchivedExpanded ? <ExpandLess /> : <ExpandMore />}
          </Box>

          {isArchivedExpanded && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
              {handbooks
                ?.filter((book) => book.archived)
                .map((book, idx) => (
                  <Box key={idx}>
                    <PdfPreviewCard
                      pdfUrl={book.presigned_url || ''}
                      handleActionAreaClick={() => {
                        setSelectedHandbook(book);
                        setIsReaderOpen(true);
                      }}
                      cardProps={{
                        sx: {
                          display: 'flex',
                          flexDirection: 'column',
                          width: 300,
                          height: '100%',
                        },
                      }}
                      contentChildren={
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                            justifyContent: 'space-around',
                          }}
                        >
                          <Typography
                            textAlign="center"
                            variant="h4"
                            color="grey"
                          >
                            ARCHIVED
                          </Typography>
                          <Typography variant="h5" align="center">
                            {formatKeyToHandbookTitle(book.key ?? '')}
                          </Typography>
                        </Box>
                      }
                      actionChildren={
                        <Stack
                          direction="row"
                          sx={{
                            width: '100%',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: 2,
                          }}
                        >
                          <Tooltip title="Download">
                            <IconButton
                              size="small"
                              onClick={() => handleDownload(book)}
                              aria-label="Download handbook"
                            >
                              <Download />
                            </IconButton>
                          </Tooltip>
                          <Button
                            size="small"
                            color="error"
                            variant="outlined"
                            onClick={() => {
                              setSelectedHandbook(book);
                              setSelectedAction('delete');
                            }}
                          >
                            Delete
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => {
                              setSelectedHandbook({
                                ...book,
                                archived: false,
                              });
                              setSelectedAction('unarchive');
                            }}
                          >
                            Unarchive
                          </Button>
                        </Stack>
                      }
                    />
                  </Box>
                ))}
            </Box>
          )}
        </Box>
      )}

      {/* Edit assignment modal */}
      {editingAssignment && (
        <EditAssignmentModal
          open={!!editingAssignment}
          assignment={editingAssignment}
          onClose={() => setEditingAssignment(null)}
          onSaved={() => {
            refreshAssignments(editingAssignment.handbook_id);
            setEditingAssignment(null);
          }}
          onRequestUnassign={() =>
            setUnassignTarget({
              assignmentId: editingAssignment.id,
              handbookId: editingAssignment.handbook_id,
              employeeName: [
                editingAssignment.first_name,
                editingAssignment.middle_initial,
                editingAssignment.last_name,
              ]
                .filter(Boolean)
                .join(' '),
            })
          }
        />
      )}

      {/* Confirm unassign dialog */}
      {unassignTarget && (
        <ConfirmUnassignDialog
          employeeName={unassignTarget.employeeName}
          onClose={() => setUnassignTarget(null)}
          onConfirm={confirmUnassign}
        />
      )}

      {/* Confirm send reminder dialog */}
      {sendReminderTarget && (
        <ConfirmSendReminderDialog
          employeeName={sendReminderTarget.employeeName}
          handbookName={sendReminderTarget.handbookName}
          onClose={() => setSendReminderTarget(null)}
          onConfirm={confirmSendReminder}
        />
      )}

      {isArmhrAdmin && (
        <HandbookPermissionsModal
          open={isPermissionsOpen}
          onClose={() => setIsPermissionsOpen(false)}
        />
      )}
    </Box>
  );
};

interface HandbookPermissionsModalProps {
  open: boolean;
  onClose: () => void;
}

const HandbookPermissionsModal: React.FC<HandbookPermissionsModalProps> = ({
  open,
  onClose,
}) => {
  const api = useApi();
  const { showNotification } = useNotifications();

  const [managers, setManagers] = useState<ManagerHandbookConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [togglingIds, setTogglingIds] = useState<Set<string>>(new Set());

  const fetchManagers = useCallback(async () => {
    try {
      setLoading(true);
      const { results } = await api.admin.getHandbookManagerConfig();
      setManagers(results);
    } catch (e) {
      showNotification({
        message: 'Failed to load manager permissions.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchManagers();
      setSearch('');
    }
  }, [open]);

  const handleToggle = async (employeeId: string, currentValue: boolean) => {
    setTogglingIds((prev) => new Set(prev).add(employeeId));
    try {
      await api.admin.setHandbookManagerConfig({
        employee_id: employeeId,
        value: !currentValue,
      });
      setManagers((prev) =>
        prev.map((m) =>
          m.employee_id === employeeId
            ? { ...m, handbook_upload_enabled: !currentValue }
            : m
        )
      );
      showNotification({
        message: !currentValue
          ? 'Handbook access granted'
          : 'Handbook access revoked',
        severity: 'success',
      });
    } catch (e) {
      showNotification({
        message: 'Failed to update permission. Please try again.',
        severity: 'error',
      });
    } finally {
      setTogglingIds((prev) => {
        const next = new Set(prev);
        next.delete(employeeId);
        return next;
      });
    }
  };

  const enabledCount = managers.filter((m) => m.handbook_upload_enabled).length;

  const filteredManagers = useMemo(() => {
    const q = search.toLowerCase().trim();
    const filtered = q
      ? managers.filter(
          (m) =>
            m.first_name?.toLowerCase().includes(q) ||
            m.last_name?.toLowerCase().includes(q) ||
            m.middle_initial?.toLowerCase().includes(q) ||
            m.employee_id?.toLowerCase().includes(q)
        )
      : managers;

    return [...filtered].sort((a, b) => {
      if (a.handbook_upload_enabled !== b.handbook_upload_enabled) {
        return a.handbook_upload_enabled ? -1 : 1;
      }
      return (a.last_name ?? '').localeCompare(b.last_name ?? '');
    });
  }, [managers, search]);

  const getInitials = (first?: string | null, last?: string | null) => {
    const f = first?.charAt(0)?.toUpperCase() ?? '';
    const l = last?.charAt(0)?.toUpperCase() ?? '';
    return f + l || '?';
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { maxHeight: '80vh' } }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
        }}
      >
        <Box>
          <Typography variant="h5" component="span">
            Manager Handbook Permissions
          </Typography>
          {!loading && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {enabledCount} of {managers.length} manager
              {managers.length !== 1 ? <span>s</span> : <span></span>}{' '}
              <span>
                with access to upload handbooks and assign them to employees
              </span>
            </Typography>
          )}
        </Box>
        <IconButton onClick={onClose} size="small" aria-label="Close">
          <Close />
        </IconButton>
      </DialogTitle>

      <Box sx={{ px: 3, pb: 1.5 }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Search by name or employee ID..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlined fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <DialogContent dividers sx={{ px: 1, pt: 0, overflowY: 'auto' }}>
        {loading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              py: 8,
            }}
          >
            <CircularProgress size={32} />
          </Box>
        ) : filteredManagers.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="body2" color="text.secondary">
              {search ? 'No managers match your search' : 'No managers found'}
            </Typography>
          </Box>
        ) : (
          <List disablePadding>
            {filteredManagers.map((manager) => {
              const isToggling = togglingIds.has(manager.employee_id);
              const isEnabled = manager.handbook_upload_enabled;

              return (
                <ListItem
                  key={manager.employee_id}
                  sx={{
                    px: 2,
                    py: 1.25,
                    borderRadius: 1,
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                  secondaryAction={
                    <LoadingButton
                      size="small"
                      variant={isEnabled ? 'outlined' : 'contained'}
                      color={isEnabled ? 'inherit' : 'primary'}
                      loading={isToggling}
                      onClick={() =>
                        handleToggle(manager.employee_id, isEnabled)
                      }
                      sx={{ minWidth: 110 }}
                    >
                      {isEnabled ? 'Revoke' : 'Grant Access'}
                    </LoadingButton>
                  }
                >
                  <ListItemAvatar>
                    <Avatar
                      sx={{
                        width: 36,
                        height: 36,
                        fontSize: '0.85rem',
                        bgcolor: isEnabled ? 'primary.main' : 'grey.400',
                      }}
                    >
                      {getInitials(manager.first_name, manager.last_name)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2" fontWeight={600}>
                          {[
                            manager.first_name,
                            manager.middle_initial,
                            manager.last_name,
                          ]
                            .filter(Boolean)
                            .join(' ')}
                        </Typography>
                        {isEnabled && (
                          <Chip
                            icon={<CheckCircleOutline sx={{ fontSize: 14 }} />}
                            label="Enabled"
                            size="small"
                            color="success"
                            variant="outlined"
                            sx={{
                              height: 22,
                              '& .MuiChip-label': {
                                px: 0.75,
                                fontSize: '0.7rem',
                              },
                            }}
                          />
                        )}
                      </Stack>
                    }
                    secondary={`ID: ${manager.employee_id}`}
                    secondaryTypographyProps={{
                      variant: 'caption',
                      color: 'text.secondary',
                    }}
                    sx={{ mr: 2 }}
                  />
                </ListItem>
              );
            })}
          </List>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button variant="outlined" onClick={onClose}>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Handbook Action Dialog (archive / delete / unarchive confirmation)
// ---------------------------------------------------------------------------

interface HandbookActionDialogProps {
  handbook: HandbookAsset;
  action: 'archive' | 'delete' | 'unarchive' | null;
  onClose: () => void;
  onArchive: (handbook: HandbookAsset) => Promise<void>;
  onDelete: (id: number) => Promise<void>;
}

const HandbookActionDialog: React.FC<HandbookActionDialogProps> = ({
  handbook,
  action,
  onClose,
  onArchive,
  onDelete,
}) => {
  if (!action) return null;

  const content = {
    archive: {
      title: 'Archive confirmation',
      description:
        'This handbook will no longer be viewable by employees but all records of signed acknowledgements will be retained.',
    },
    delete: {
      title: 'Delete confirmation',
      description:
        'This action cannot be undone and will erase all progress for those assigned.',
    },
    unarchive: {
      title: 'Unarchive confirmation',
      description: 'This handbook will be viewable by employees again.',
    },
  };

  const isArchive = action === 'archive' || action === 'unarchive';

  const handleAction = () => {
    if (isArchive) {
      onArchive(handbook);
    } else {
      onDelete(handbook.id);
    }
    onClose();
  };

  return (
    <Dialog open={true} maxWidth="sm">
      <DialogTitle>{content[action].title}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="body1">
            Are you sure you would like to {action}{' '}
            {formatKeyToHandbookTitle(handbook.key)}?
          </Typography>
          <Typography variant="body1">{content[action].description}</Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color={action === 'delete' ? 'error' : 'primary'}
          onClick={handleAction}
        >
          {action === 'delete'
            ? 'Delete'
            : action === 'archive'
            ? 'Archive'
            : 'Unarchive'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Confirm Unassign Dialog
// ---------------------------------------------------------------------------

interface ConfirmUnassignDialogProps {
  employeeName: string;
  onClose: () => void;
  onConfirm: () => void;
}

const ConfirmUnassignDialog: React.FC<ConfirmUnassignDialogProps> = ({
  employeeName,
  onClose,
  onConfirm,
}) => {
  const [loading, setLoading] = useState(false);

  const handleConfirm = async () => {
    setLoading(true);
    await onConfirm();
    setLoading(false);
  };

  return (
    <Dialog open={true} onClose={onClose} maxWidth="sm">
      <DialogTitle>Unassign confirmation</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="body1">
            Are you sure you would like to unassign{' '}
            <strong>{employeeName}</strong>?
          </Typography>
          <Typography variant="body1">
            This will remove the assignment and any progress.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          color="error"
          onClick={handleConfirm}
          loading={loading}
        >
          Unassign
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Confirm Send Reminder Dialog
// ---------------------------------------------------------------------------

interface ConfirmSendReminderDialogProps {
  employeeName: string;
  handbookName: string;
  onClose: () => void;
  onConfirm: () => void;
}

const ConfirmSendReminderDialog: React.FC<ConfirmSendReminderDialogProps> = ({
  employeeName,
  handbookName,
  onClose,
  onConfirm,
}) => {
  const [loading, setLoading] = useState(false);

  const handleConfirm = async () => {
    setLoading(true);
    await onConfirm();
    setLoading(false);
  };

  return (
    <Dialog open onClose={onClose} maxWidth="xs">
      <DialogTitle>Send reminder</DialogTitle>
      <DialogContent>
        <Typography variant="body1">
          Send a reminder email to <strong>{employeeName}</strong> for{' '}
          <strong>{handbookName}</strong>?
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          onClick={handleConfirm}
          loading={loading}
        >
          Send reminder
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Reminder Dialog
// ---------------------------------------------------------------------------

interface ReminderDialogProps {
  handbook: HandbookAsset;
  onClose: () => void;
  onSave: (days: number | null) => Promise<void>;
}

const ReminderDialog: React.FC<ReminderDialogProps> = ({
  handbook,
  onClose,
  onSave,
}) => {
  const currentDays = (handbook.config as Record<string, unknown> | null)
    ?.reminder_days_before_due as number | undefined;
  const [selected, setSelected] = useState<string>(
    currentDays != null ? String(currentDays) : 'none'
  );
  const [loading, setLoading] = useState(false);

  const handleSave = async () => {
    setLoading(true);
    await onSave(selected === 'none' ? null : Number(selected));
    setLoading(false);
  };

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Set reminder email</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Send a reminder to employees who have not started or are still pending{' '}
          <strong>{formatKeyToHandbookTitle(handbook.key)}</strong>.
        </Typography>
        <RadioGroup
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
        >
          {[1, 2, 3].map((d) => (
            <FormControlLabel
              key={d}
              value={String(d)}
              control={<Radio size="small" />}
              label={`${d} day${d > 1 ? 's' : ''} before due date`}
            />
          ))}
          <FormControlLabel
            value="none"
            control={<Radio size="small" />}
            label="No reminder (clear)"
          />
        </RadioGroup>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          onClick={handleSave}
          loading={loading}
        >
          Save
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default AdminHandbooksPage;
